import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  BulkGroupAddRequest,
  BulkGroupRemoveRequest,
  GroupCreateRequest,
  GroupDetail,
  GroupRow,
  GroupUpdateRequest,
  UserInGroupRow,
} from './types';

export const groupKeys = {
  all: ['groups'] as const,
  list: () => ['groups', 'list'] as const,
  detail: (groupId: string | number) => ['groups', 'detail', groupId] as const,
  members: (groupId: string | number) => ['groups', 'members', groupId] as const,
};

/* List ------------------------------------------------------------ */

export function useGroupsList() {
  return useQuery({
    queryKey: groupKeys.list(),
    queryFn: () => api.get<GroupRow[]>('/api/obd/groups'),
    staleTime: 30_000,
    select: (data) => ensureArray<GroupRow>(data),
  });
}

/* Detail (used by update form prefill) ---------------------------- */

export function useGroupDetail(groupId: string | number | null) {
  return useQuery({
    queryKey: groupKeys.detail(groupId ?? 'none'),
    queryFn: () => api.get<GroupDetail>(`/api/obd/group/detail/${groupId}`),
    enabled: Boolean(groupId),
    staleTime: 30_000,
  });
}

/* Mutations ------------------------------------------------------- */

function useInvalidateGroups() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: groupKeys.all });
}

export function useCreateGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (req: GroupCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/group', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (req: GroupUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/group/update', req),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (groupId: number) =>
      api.delete<{ message?: string }>(`/api/obd/group/${groupId}`),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Bulk operations ---------------- */

/** POST /api/obd/user/groups/list — users currently inside a group. */
export function useUsersInGroup(groupId: string | number | null) {
  return useQuery({
    queryKey: groupKeys.members(groupId ?? 'none'),
    queryFn: () =>
      api.post<UserInGroupRow[]>('/api/obd/user/groups/list', {
        groupId: String(groupId),
      }),
    enabled: Boolean(groupId),
    staleTime: 30_000,
    select: (data) => ensureArray<UserInGroupRow>(data),
  });
}

export function useBulkAddGroupUsers() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (req: BulkGroupAddRequest) =>
      api.post<{ message?: string }>('/api/obd/bulk/user/group/addition', req),
    onSuccess: () => invalidate(),
  });
}

export function useBulkRemoveGroupUsers() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (req: BulkGroupRemoveRequest) =>
      api.post<{ message?: string }>('/api/obd/bulk/user/group/deletion', {
        ...req,
        swapGroupId: req.swapGroupId ?? '',
      }),
    onSuccess: () => invalidate(),
  });
}

/**
 * POST /api/obd/groups/update — bulk-update properties on an existing group
 * (currently the legacy form sends `{ groupId, groupName, status }`).
 * The endpoint accepts an open key/value record so callers can extend.
 */
export function useGroupsBulkUpdate() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ message?: string }>('/api/obd/groups/update', body),
    onSuccess: () => invalidate(),
  });
}
